import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowDown } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import NewsletterSignup from "@/components/NewsletterSignup";
import mmFoundationLogo from "@/assets/mm-foundation.png.asset.json";
import { useLang, type Language } from "@/i18n";


const Footer = () => {
  const { market, lang, setLanguage, t, route } = useLang();
  const quickLinks = market === "intl"
    ? [
        { name: t("nav.map"), path: route("map") },
        { name: t("nav.archetype"), path: route("archetype") },
        { name: t("nav.letters"), path: route("letters") },
        { name: t("nav.about"), path: route("about") },
      ]
    : [
        { name: "Pamokos", path: "/pamokos" },
        { name: "Knygynas", path: "/knygynas" },
        { name: "Meditacijos", path: "https://www.meditacijos.com", external: true },
        { name: "Aimana", path: "https://www.aimana.lt", external: true },
        { name: "Blogas", path: "/blogas" },
        { name: "Apie", path: "/apie" },
      ];
  const languages: { label: string; value: Language }[] = [
    { label: "LT", value: "lt" },
    { label: "EN", value: "en" },
  ];

  return (
    <footer className="relative z-40 bg-[hsl(var(--footer-bg))] text-[hsl(var(--footer-fg))]">
      <div className="container mx-auto px-6 py-16 md:py-20">
        {/* Brand row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 pb-10 border-b border-white/10">
          <Link to="/" className="inline-flex items-center">
            <BrandMark variant="light" sizeClassName="h-12 md:h-14" />
          </Link>
          <p className="text-sm md:text-base italic text-[hsl(var(--footer-fg))]/60 md:text-right">
            {t("brand.tagline")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12 pb-12 border-b border-white/10 items-start">
          {/* Quick Links */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--footer-fg))]/55 mb-5">
              {t("footer.links")}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.path}>
                  {"external" in item && item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-[hsl(var(--footer-fg))] hover:text-terracotta transition-colors"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="text-base text-[hsl(var(--footer-fg))] hover:text-terracotta transition-colors"
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--footer-fg))]/55 mb-5">
              {t("footer.contact")}
            </h3>
            <a
              href={market === "intl" ? "mailto:hello@humanmostly.com" : "mailto:info@laimesdieta.lt"}
              className="text-base text-[hsl(var(--footer-fg))] hover:text-terracotta transition-colors"
            >
              {market === "intl" ? "hello@humanmostly.com" : "info@laimesdieta.lt"}
            </a>
            {market === "lt" && <div className="flex items-center gap-3 mt-5">
              <a
                href="https://www.instagram.com/laimesdieta"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.facebook.com/laimesdieta"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
              >
                <Facebook size={16} />
              </a>
            </div>}
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--footer-fg))]/55 mb-5">
              {t("footer.letters")}
            </h3>
            <NewsletterSignup variant="footer" />
          </div>

          {/* M&M Foundation logo */}
          {market === "lt" ? <div className="flex md:justify-end">
            <a
              href="https://mmfoundation.lt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="M&M Foundation"
              className="inline-flex transition-opacity hover:opacity-80"
            >
              <img
                src={mmFoundationLogo.url}
                alt="M&M Foundation"
                className="h-40 md:h-48 w-auto object-contain [filter:brightness(0)_invert(1)] opacity-95"
                loading="lazy"
              />
            </a>
          </div> : <div className="flex md:justify-end">
            <div className="text-sm text-[hsl(var(--footer-fg))]/55 leading-relaxed md:text-right max-w-xs">
              Human, Mostly is published by The Publisher Co.
            </div>
          </div>}
        </div>


        {/* Bottom bar */}
        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">

          <p className="text-xs text-[hsl(var(--footer-fg))]/45">
            © {new Date().getFullYear()} {t("brand.name")} · {t("footer.rights")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-[hsl(var(--footer-fg))]/45">
            <Link to={route("terms")} className="hover:text-terracotta transition-colors">
              {t("footer.terms")}
            </Link>
            <Link to={route("privacy")} className="hover:text-terracotta transition-colors">
              {t("footer.privacy")}
            </Link>
            <span className="inline-flex items-center gap-2">
              {languages.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setLanguage(option.value)}
                  className={option.value === lang ? "text-terracotta" : "hover:text-terracotta transition-colors"}
                >
                  {option.label}
                </button>
              ))}
            </span>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label={t("footer.top")}
            className="w-10 h-10 rounded-full bg-cream text-ink flex items-center justify-center hover:bg-cream-soft transition-colors rotate-180"
          >
            <ArrowDown size={16} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
